// Invisible reCAPTCHA Enterprise used by Onboarding. The backend verifies the token.
export type RecaptchaAction = 'signup_start' | 'signup';

interface Grecaptcha {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: RecaptchaAction }) => Promise<string>;
  };
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

const SCRIPT_ID = 'recaptcha-script';
// Long enough for a slow connection, short enough that a stalled Google doesn't
// hold the step's buttons disabled.
const TIMEOUT_MS = 5000;
let scriptLoaded: Promise<void> | null = null;

export const loadRecaptcha = (key: string) => {
  if (document.getElementById(SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${key}`;
  script.async = true;
  // Resolves on error too; a missing grecaptcha then returns null below.
  scriptLoaded = new Promise((resolve) => {
    script.onload = script.onerror = () => resolve();
  });
  document.head.appendChild(script);
};

const fetchToken = async (key: string, action: RecaptchaAction): Promise<string | null> => {
  await scriptLoaded;
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) {
    return null;
  }
  await new Promise<void>((resolve) => enterprise.ready(resolve));
  return await enterprise.execute(key, { action });
};

// Resolves to null when the script is blocked, Google fails or it takes too long;
// the backend decides what a missing token means.
export const getRecaptchaToken = async (
  siteKey: string | null | undefined,
  action: RecaptchaAction
): Promise<string | null> => {
  if (!siteKey) {
    return null;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), TIMEOUT_MS);
  });
  try {
    return await Promise.race([fetchToken(siteKey, action), timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
